<template>
  <div>
    <div v-tooltip="cardTooltip" class="flex items-center">
      <img v-if="logoUrl" :src="logoUrl" :width="logoWidth" class="align-top inline-flex" />
      <TwcIcon v-else icon="credit-card-blank" size="xl" :class="iconClasses" />
      <i v-if="isApplePay" class="fa-brands fa-apple-pay fa-xl ml-2"></i>
      <i v-if="isGooglePay" class="fa-brands fa-google-pay fa-xl ml-2"></i>
      <span class="ml-2 align-middle whitespace-nowrap">{{ cardDescription }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { TwcIcon } from 'transit-web-components'
import { AVAILABLE_CC_ICONS, MAPPED_CC_NETWORKS, MAPPED_CC_GENERIC_LOGO } from '@/utils/constants'
import { capitalizeFirstLetter } from '@/utils/text'

const props = defineProps({
  card: {
    type: Object as () => Partial<PaymentCard>,
    required: true
  },
  simple: {
    type: Boolean,
    default: false
  },
  logoSize: {
    type: String,
    default: 'small'
  },
  wallet: {
    type: String,
    default: ''
  }
})

const imageBucketUrl = import.meta.env.VITE_S3_BUCKET_URL
const logoUrl = computed(() => {
  return AVAILABLE_CC_ICONS.includes(props.card.network || '')
    ? `${imageBucketUrl}/images/payment/cof_${props.card.network}.png`
    : ''
})
const expirationYear = computed(() => {
  return (props.card.expYear || '').toString().slice(-2)
})
const fundingName = computed(() => {
  return props.card.funding == 'credit' ? '' : capitalizeFirstLetter(props.card.funding || '')
})
const cardTooltip = computed(() => {
  let tooltip =
    MAPPED_CC_NETWORKS[props.card.network || ''] || capitalizeFirstLetter(props.card.network || '')

  if (props.card.funding != 'credit') {
    tooltip += ` ${capitalizeFirstLetter(props.card.funding || '')}`
  }

  return tooltip
})
const iconClasses = computed(() => {
  let base = 'ps-1'

  if (props.logoSize == 'small') {
    base += ' w-[30px]'
  } else {
    base += ' w-[40px]'
  }

  return base
})
const logoWidth = computed(() => {
  if (props.logoSize == 'small') {
    return 30
  } else {
    return 40
  }
})
const cardName = computed(() => {
  return MAPPED_CC_GENERIC_LOGO[props.card.network || ''] || ''
})
const cardDescription = computed(() => {
  if (props.simple) {
    const ccName = `${cardName.value} ${fundingName.value}`.trim()
    return `${ccName} •••• ${props.card.last4}`.trim()
  } else {
    return `•••• ${props.card.last4} (Exp ${props.card.expMonth}/${expirationYear.value})`
  }
})
const isApplePay = computed(() => props.wallet == 'Apple Wallet')
const isGooglePay = computed(() => props.wallet == 'Google Wallet')
</script>

<style scoped></style>
